import { ApiCore } from '@/services/api/utils/core.js'
import { apiProvider } from '@/services/api/utils/provider.js'
import axios from 'axios'
import { handleResponse, handleError } from '@/services/api/utils/response.js'

const url = 'data-element-versions'

const api = new ApiCore({
	index: true,
	url: url
})

/**
 * GET /data-element-versions?dataElementId=&version=&status=&type=&all=true&currentPage=&pageSize=
 * List data element versions with filters and pagination
 * Response: { data: { items: DataElementVersion[], meta: { currentPage, pageSize, pageCount, count } } }
 */
api.index = (params) => {
	return axios
		.get(`${apiProvider.BASE_URL}/${url}?${params}`)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * GET /data-element-versions?dataElementId={id}&all=true
 * Get all versions for a specific data element (for detail/version-history page)
 */
api.getAllVersions = (dataElementId) => {
	return axios
		.get(`${apiProvider.BASE_URL}/${url}?dataElementId=${dataElementId}&all=true`)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * GET /data-element-versions/:dataElementId/versions/:version
 * Get a single DataElementVersion by dataElementId and version number
 * Response: DataElementVersion object
 */
api.getByVersion = (dataElementId, version) => {
	return axios
		.get(`${apiProvider.BASE_URL}/${url}/${dataElementId}/versions/${version}`)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * POST /data-element-versions
 * Create initial data element versions (batch)
 * Body: IDataElementVersion[] (id, name, schema, version, status, changeType, changeDescription, type, expiry)
 * Response: "Successfully Inserted : <ids>"
 */
api.createInitial = (payload) => {
	return axios
		.post(`${apiProvider.BASE_URL}/${url}`, payload)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * POST /data-element-versions/:dataElementId/versions
 * Create new version for existing data element
 * Body: { name, schema, querySchema, version, status, changeType, changeDescription, expiry, type }
 * Response: DataElementVersion object
 */
api.createNewVersion = (dataElementId, payload) => {
	return axios
		.post(`${apiProvider.BASE_URL}/${url}/${dataElementId}/versions`, payload)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * POST /data-element-versions/:dataElementId/versions/:version/promote
 * Promote version status: DRAFT → ACTIVE → DEPRECATED → RETIRED
 * Body: { targetStatus: 'ACTIVE' | 'DEPRECATED' | 'RETIRED', expiryDate?: ISO8601 string }
 * Response: Updated DataElementVersion object
 */
api.promoteVersion = (dataElementId, version, payload) => {
	return axios
		.post(`${apiProvider.BASE_URL}/${url}/${dataElementId}/versions/${version}/promote`, payload)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * GET /data-element-versions/:dataElementId/versions/:version/impact-analysis
 * Get impact analysis for a version
 * Response: { riskScore, affectedOrganizations, integrationsCount, estimatedMigrationTime, breakingChanges, recommendations }
 */
api.getImpactAnalysis = (dataElementId, version) => {
	return axios
		.get(`${apiProvider.BASE_URL}/${url}/${dataElementId}/versions/${version}/impact-analysis`)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * PATCH /data-element-versions/:dataElementId/versions/:version
 * Patch a DRAFT version (autosave schema changes)
 * Body: { schema: {...}, updatedAt: "ISO string" }
 * Response: Updated DataElementVersion object
 * Returns 409 on optimistic-locking conflict
 *
 * Custom error handler preserves HTTP status so callers can branch on 409
 * (the shared handleError unwraps to the inner error body and drops status).
 */
api.patchVersion = (dataElementId, version, payload) => {
	return axios
		.patch(`${apiProvider.BASE_URL}/${url}/${dataElementId}/versions/${version}`, payload)
		.then(handleResponse)
		.catch((error) => {
			const status = error?.response?.status;
			const innerError = error?.response?.data?.data?.error
				|| error?.response?.data?.error;
			const message = innerError?.message
				|| (typeof innerError === 'string' ? innerError : null)
				|| error?.message
				|| 'Patch failed';
			return [{ status, message, response: error?.response }, undefined];
		});
}

/**
 * POST /data-elements
 * Create a new data element (parent record)
 * Body: { name, type }
 * Response: DataElement object
 */
api.createDataElement = (payload) => {
	return axios
		.post(`${apiProvider.BASE_URL}/data-elements`, payload)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * GET /data-element-version-comments?dataElementId={id}&versionId={version}
 * Get comments for a specific version
 */
api.getComments = (dataElementId, versionId) => {
	return axios
		.get(`${apiProvider.BASE_URL}/data-element-version-comments?dataElementId=${dataElementId}&versionId=${versionId}`)
		.then(handleResponse)
		.catch(handleError);
}

/**
 * POST /data-element-version-comments
 * Create a comment on a version
 * Body: { dataElementId, versionId, commentText, parentCommentId? }
 */
api.createComment = (payload) => {
	return axios
		.post(`${apiProvider.BASE_URL}/data-element-version-comments`, payload)
		.then(handleResponse)
		.catch(handleError);
}

export default api
